// Copyright 2025, University of Maryland, All Rights Reserved
import axios from 'axios';
import { axiosInstance } from './client';

export interface ExportResult {
  status: 'processing' | 'running' | 'completed' | 'failed';
  export_id?: string;
  org_id?: string;
  ws_id?: string;
  dynamo_count?: number;
  s3_count?: number;      // total referenced objects
  blob_count?: number;    // unique objects stored (after dedup)
  download_url?: string;
  filename?: string;
  error?: string;
}

export interface ImportPreview {
  org_id?: string;
  ws_id?: string;
  dynamo_count?: number;
  s3_count?: number;
  source_table?: string;
}

export interface ImportResult {
  status: 'processing' | 'running' | 'completed' | 'failed';
  dynamo_written?: number;
  dynamo_skipped?: number;
  s3_uploaded?: number;   // unique blobs uploaded
  s3_copied?: number;     // deduped refs materialized via CopyObject
  s3_total?: number;      // uploaded + copied
  error?: string;
}

export const adminApi = {
  // Enqueue an export job → returns { job_id, export_id } to poll.
  async exportWorkspace(org_id: string, ws_id: string, user_id: string): Promise<{ job_id: string; export_id: string }> {
    const response = await axiosInstance.post('/admin/workspace-export', { org_id, ws_id }, { params: { user_id } });
    return response.data;
  },

  async getExportResult(export_id: string): Promise<ExportResult> {
    const response = await axiosInstance.get(`/admin/workspace-export/${export_id}`);
    return response.data;
  },

  // Presigned URL to upload a bundle before importing.
  async requestImportUploadUrl(filename: string): Promise<{ upload_url: string; bundle_key: string }> {
    const response = await axiosInstance.post('/admin/workspace-import/upload-url', { filename });
    return response.data;
  },

  // Upload the bundle directly to S3 via the presigned URL.
  // The presigned URL is signed with ContentType + Tagging, so the PUT MUST send
  // both matching headers or S3 rejects it with a 403 (signature mismatch).
  async uploadBundle(uploadUrl: string, file: File): Promise<void> {
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': 'application/zip',
        'x-amz-tagging': 'object_lifecycle=lifecycle_archive',
      },
    });
  },

  // Read the bundle manifest (no writes) for a confirm-before-import preview.
  async previewImport(bundle_key: string): Promise<ImportPreview> {
    const response = await axiosInstance.post('/admin/workspace-import/preview', { bundle_key });
    return response.data;
  },

  // Enqueue an import job → returns { job_id, import_id } to poll.
  async importWorkspace(
    bundle_key: string,
    org_id: string,
    ws_id: string,
    user_id: string,
    overwrite = true
  ): Promise<{ job_id: string; import_id: string }> {
    const response = await axiosInstance.post(
      '/admin/workspace-import',
      { bundle_key, org_id, ws_id, overwrite },
      { params: { user_id } }
    );
    return response.data;
  },

  async getImportResult(import_id: string): Promise<ImportResult> {
    const response = await axiosInstance.get(`/admin/workspace-import/${import_id}`);
    return response.data;
  },
};
